import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api, { getClientes, getMantenimientos, getPendientes, getInformesPendientes } from "../api";
import { useCliente } from "../context/ClienteContext";
import { MODULE_IDS } from "./moduleIds";
import ModuleHeader from "../components/ui/ModuleHeader";
import "./DashboardSupervisorModule.css";

const CATALOG = {
  overview: { title: "Resumen operativo", description: "Indicadores generales", width: "wide" },
  agents: { title: "Agentes", description: "Conectividad de Windows, NAS y pfSense", width: "small" },
  backups: { title: "Estado de copias", description: "Resultado de las tareas Windows activas", width: "small" },
  attention: { title: "Requiere atención", description: "Riesgos detectados por los agentes", width: "wide" },
  updates: { title: "Actualizaciones", description: "Equipos con software pendiente", width: "wide" },
  maintenance: { title: "Mantenimientos pendientes", description: "Trabajo que requiere atención", width: "wide" },
  reports: { title: "Informes pendientes", description: "Revisión y envío", width: "small" },
  avisos: { title: "Avisos en trámite", description: "Asistencias abiertas sincronizadas con Treyder", width: "wide" },
  followups: { title: "Seguimientos", description: "Intervenciones que requieren continuidad", width: "wide" },
};
const DEFAULT_LAYOUT = {
  widgets: [
    { id: "overview", width: "wide" },
    { id: "attention", width: "wide" },
    { id: "avisos", width: "wide" },
    { id: "agents", width: "small" },
    { id: "backups", width: "small" },
    { id: "updates", width: "wide" },
    { id: "reports", width: "small" },
    { id: "maintenance", width: "wide" },
    { id: "followups", width: "wide" },
  ],
};

function rows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}
function cleanLayout(value) {
  const requested = Array.isArray(value?.widgets) ? value.widgets : [];
  const unique = new Set();
  const widgets = requested
    .filter((item) => CATALOG[item?.id] && !unique.has(item.id) && unique.add(item.id))
    .map((item) => ({ id: item.id, width: item.width === "wide" ? "wide" : "small" }));
  return { widgets: widgets.length ? widgets : DEFAULT_LAYOUT.widgets };
}
function displayName(item) {
  return item?.cliente || item?.nombre_cliente || item?.client_name || item?.descripcion || item?.ordenador || "Sin identificar";
}
function statusLabel(agent) {
  if (agent.enabled === 0 || agent.enabled === false) return "Retirado";
  return agent.agent_status === "ONLINE" ? "En línea" : "Sin conexión";
}
function versionIsOlder(installed, published) {
  const current = String(installed || "").match(/\d+/g)?.slice(0, 4).map(Number);
  const target = String(published || "").match(/\d+/g)?.slice(0, 4).map(Number);
  if (!current?.length || !target?.length) return false;
  const length = Math.max(current.length, target.length);
  for (let index = 0; index < length; index += 1) {
    if ((current[index] || 0) < (target[index] || 0)) return true;
    if ((current[index] || 0) > (target[index] || 0)) return false;
  }
  return false;
}

function Widget({ item, editing, onMove, onRemove, onToggleWidth, onDragStart, onDragOver, onDrop, children }) {
  const meta = CATALOG[item.id];
  const ref = useRef(null);
  const [rows, setRows] = useState(1);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setRows(Math.max(1, Math.ceil((node.getBoundingClientRect().height + 16) / 24)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children, editing, item.width]);
  return <section ref={ref} style={{ gridRowEnd: `span ${rows}` }} className={"card dashboard-widget dashboard-widget--" + item.width + (editing ? " is-editing" : "")} draggable={editing} onDragStart={(event) => onDragStart(event, item.id)} onDragOver={(event) => onDragOver(event)} onDrop={(event) => onDrop(event, item.id)}>
    <header className="dashboard-widget__head">
      <div><h2>{meta.title}</h2><p>{meta.description}</p></div>
      {editing ? <div className="dashboard-widget__tools">
        <button type="button" className="button secondary small" onClick={() => onMove(item.id, -1)} aria-label={"Mover " + meta.title + " antes"}>←</button>
        <button type="button" className="button secondary small" onClick={() => onMove(item.id, 1)} aria-label={"Mover " + meta.title + " después"}>→</button>
        <button type="button" className="button secondary small" onClick={() => onToggleWidth(item.id)} title={item.width === "wide" ? "Usar media anchura" : "Usar ancho completo"}>{item.width === "wide" ? "½ ancho" : "↔ ancho"}</button><button type="button" className="button danger small" onClick={() => onRemove(item.id)}>Quitar</button>
      </div> : null}
    </header>
    <div className="dashboard-widget__body">{children}</div>
  </section>;
}

export default function DashboardSupervisorModule() {
  const { seleccionarCliente, setActiveModule, setPcActivo, setMaintenanceFocus } = useCliente();
  const [data, setData] = useState({ clients: [], pending: [], reports: [], maintenance: [], avisos: [], agents: [], releases: {}, backupStatus: {}, backupPlans: [], rmmSummary: { attention: [], updates: [], counts: {} } });
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draggingId, setDraggingId] = useState("");
  const [backupDetail, setBackupDetail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.allSettled([
        getClientes(), getMantenimientos(), getPendientes(), getInformesPendientes(),
        api.get("/backup-monitor/agents"),
        api.get("/backup-monitor/nas-agents"),
        api.get("/monitorizacion/pfsense/agents"),
        api.get("/backup-monitor/agent-releases"),
        api.get("/backup-monitor/status"),
        api.get("/backup-monitor/plans"),
        api.get("/rmm/summary"),
        api.get("/dashboard/layout", { params: { scope: "global" } }),
        api.get("/avisos"),
      ]);
      const dataAt = (index) => responses[index]?.status === "fulfilled" ? responses[index].value?.data : null;
      const clients = dataAt(0), maintenance = dataAt(1), pending = dataAt(2), reports = dataAt(3), windows = dataAt(4), nas = dataAt(5), pfsense = dataAt(6), published = dataAt(7), backupStatus = dataAt(8), backupPlans = dataAt(9), rmmSummary = dataAt(10), saved = dataAt(11), avisos = dataAt(12);
      setData({
        clients: rows(clients), maintenance: rows(maintenance), pending: rows(pending), reports: rows(reports), avisos: rows(avisos?.avisos || avisos),
        agents: [...rows(windows?.agents).map((agent) => ({ ...agent, platform: "Windows", platformKey: "windows" })), ...rows(nas?.agents).map((agent) => ({ ...agent, platform: "NAS", platformKey: "nas" })), ...rows(pfsense?.agents).map((agent) => ({ ...agent, platform: "pfSense", platformKey: "pfsense" }))],
        releases: Object.fromEntries((published?.releases || []).map((release) => [release.platform, release.version])),
        backupStatus: backupStatus || {}, backupPlans: rows(backupPlans?.plans), rmmSummary: rmmSummary || { attention: [], updates: [], counts: {} },
      });
      setLayout(cleanLayout(saved?.layout || DEFAULT_LAYOUT));
    } catch (reason) {
      setError(reason?.response?.data?.error || "No se pudo cargar el Dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLayout = async (next) => {
    const safe = cleanLayout(next);
    setLayout(safe);
    setSaving(true);
    try {
      await api.put("/dashboard/layout", { scope: "global", layout: safe });
    } catch (reason) {
      setError(reason?.response?.data?.error || "No se pudo guardar tu diseño.");
    } finally {
      setSaving(false);
    }
  };
  const move = (id, delta) => {
    const current = [...layout.widgets];
    const index = current.findIndex((item) => item.id === id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= current.length) return;
    [current[index], current[target]] = [current[target], current[index]];
    saveLayout({ widgets: current });
  };
  const remove = (id) => saveLayout({ widgets: layout.widgets.filter((item) => item.id !== id) });
  const toggleWidth = (id) => saveLayout({ widgets: layout.widgets.map((item) => item.id === id ? { ...item, width: item.width === "wide" ? "small" : "wide" } : item) });
  const startDrag = (event, id) => { setDraggingId(id); event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", id); };
  const dragOver = (event) => { if (editing) event.preventDefault(); };
  const dropOn = (event, targetId) => { event.preventDefault(); const id = event.dataTransfer.getData("text/plain") || draggingId; setDraggingId(""); if (!id || id === targetId) return; const widgets = [...layout.widgets]; const from = widgets.findIndex((item) => item.id === id), to = widgets.findIndex((item) => item.id === targetId); if (from < 0 || to < 0) return; const [moved] = widgets.splice(from, 1); widgets.splice(to, 0, moved); saveLayout({ widgets }); };
  const add = (id) => saveLayout({ widgets: [...layout.widgets, { id, width: CATALOG[id].width }] });
  const reset = () => saveLayout(DEFAULT_LAYOUT);

  const model = useMemo(() => {
    const activeAgents = data.agents.filter((agent) => agent.enabled !== 0 && agent.enabled !== false);
    const onlineAgents = activeAgents.filter((agent) => agent.agent_status === "ONLINE");
    const offlineAgents = activeAgents.filter((agent) => agent.agent_status !== "ONLINE");
    const pendingAgentUpdates = activeAgents.filter((agent) => versionIsOlder(agent.version, data.releases[agent.platformKey]));
    const followups = data.maintenance.filter((item) => ["follow_up", "blocked", "partial", "not_completed"].includes(String(item.status || "")));
    const avisos = data.avisos.filter((item) => String(item?.estado || "").trim().toLocaleLowerCase("es") === "en tramite").sort((a, b) => Number(b.numero_aviso || b.treyder_id || 0) - Number(a.numero_aviso || a.treyder_id || 0));
    const urgentAvisos = avisos.filter((item) => /urg|crit/i.test(String(item?.prioridad || "")));
    const pendingByClient = [...data.pending.reduce((map, item) => {
      const id = Number(item?.cliente_id) || String(item?.cliente || "Sin cliente");
      const current = map.get(id) || { cliente_id: Number(item?.cliente_id) || null, cliente: item?.cliente || "Sin cliente", count: 0 };
      current.count += 1;
      map.set(id, current);
      return map;
    }, new Map()).values()].sort((a, b) => b.count - a.count || a.cliente.localeCompare(b.cliente, "es"));
    return {
      activeAgents, onlineAgents, offlineAgents, pendingAgentUpdates, followups, avisos, urgentAvisos, pendingByClient,
      pending: pendingByClient.slice(0, 8),
      reports: data.reports.slice(0, 6),
      totalClients: data.clients.length,
      backupStatus: data.backupStatus,
      backupPlans: data.backupPlans,
      attention: Array.isArray(data.rmmSummary?.attention) ? data.rmmSummary.attention : [],
      updates: Array.isArray(data.rmmSummary?.updates) ? data.rmmSummary.updates : [],
      attentionCount: Number(data.rmmSummary?.counts?.attention || 0),
      updateCount: Number(data.rmmSummary?.counts?.updates || 0),
    };
  }, [data]);

  const openMaintenance = async (item) => {
    const clientId = Number(item?.cliente_id || item?.client_id || item?.id_cliente);
    if (Number.isInteger(clientId) && clientId > 0) await seleccionarCliente(clientId);
    if (item?.ordenador_id) setPcActivo(item.ordenador_id);
    if (item?.id && item?.ordenador_id) setMaintenanceFocus({ maintenanceId: item.id, pcId: item.ordenador_id });
    setActiveModule(MODULE_IDS.MANTENIMIENTOS);
  };
  const openReports = () => setActiveModule(MODULE_IDS.INFORMES);
  const openAvisos = () => setActiveModule(MODULE_IDS.AVISOS);
  const openAgents = () => setActiveModule(MODULE_IDS.PFSENSE_AGENTS);
  const openAgent = async (agent) => {
    try { window.sessionStorage.setItem("megaprint.agents.focus", JSON.stringify({ id: String(agent.id), type: agent.platformKey })); } catch { /* Navigation can still continue. */ }
    const clientId = Number(agent.client_id);
    if (Number.isInteger(clientId) && clientId > 0) await seleccionarCliente(clientId);
    setActiveModule(MODULE_IDS.PFSENSE_AGENTS);
  };
  const openBackups = () => setActiveModule(MODULE_IDS.BACKUPS_NEW);
  const openBackupPlan = async (plan) => {
    const clientId = Number(plan?.client_id);
    if (Number.isInteger(clientId) && clientId > 0) await seleccionarCliente(clientId);
    setActiveModule(MODULE_IDS.BACKUPS_NEW);
  };
  const openDevice = async (item) => {
    const clientId = Number(item?.client_id);
    if (Number.isInteger(clientId) && clientId > 0) await seleccionarCliente(clientId);
    if (item?.device_id) setPcActivo(item.device_id);
    setActiveModule(MODULE_IDS.EQUIPOS);
  };

  const renderWidget = (item) => {
    const widgetProps = { item, editing, onMove: move, onRemove: remove, onToggleWidth: toggleWidth, onDragStart: startDrag, onDragOver: dragOver, onDrop: dropOn };
    if (item.id === "overview") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-kpis">
        <div><span>Clientes</span><strong>{model.totalClients}</strong></div>
        <div><span>Equipos pendientes</span><strong>{data.pending.length}</strong></div>
        <div><span>Informes pendientes</span><strong>{data.reports.length}</strong></div>
        <div><span>Requieren atención</span><strong className={model.attentionCount ? "is-warn" : ""}>{model.attentionCount}</strong></div>
      </div>
    </Widget>;
    if (item.id === "agents") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-stat"><strong>{model.onlineAgents.length} <small>en línea</small></strong><span>{model.activeAgents.length} agentes activos · Windows, NAS y pfSense</span></div>
      {model.pendingAgentUpdates.length ? <div className="dashboard-list dashboard-list--updates">{model.pendingAgentUpdates.slice(0, 4).map((agent) => <button type="button" key={"update-" + agent.platformKey + "-" + agent.id} onClick={() => openAgent(agent)}><span><strong>{agent.client_name || "Cliente"} · {agent.name}</strong><small>{agent.platform} {agent.version || "sin versión"} · disponible {data.releases[agent.platformKey]}</small></span><em>Actualizar →</em></button>)}</div> : <p className="dashboard-empty">Todos los agentes están actualizados.</p>}
      {model.offlineAgents.length ? <div className="dashboard-list">{model.offlineAgents.slice(0, 4).map((agent) => <button type="button" key={agent.id} onClick={openAgents}><span>{agent.client_name || "Cliente"} · {agent.name}<small>{agent.platform || "Agente"}</small></span><em>{statusLabel(agent)}</em></button>)}</div> : <p className="dashboard-empty">Todos los agentes activos están en línea.</p>}
    </Widget>;
    if (item.id === "attention") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-list">{model.attention.length ? model.attention.slice(0, 6).map((entry, index) => <button type="button" key={`${entry.device_id}-${entry.type}-${index}`} onClick={() => openDevice(entry)}><span><strong>{entry.client_name} · {entry.device_name}</strong><small>{entry.title} · {entry.detail}</small></span><em className={entry.severity === "critical" ? "dashboard-severity-critical" : ""}>{entry.severity === "critical" ? "Crítico" : "Revisar"}</em></button>) : <p className="dashboard-empty">No hay alertas operativas informadas por los agentes.</p>}</div>
    </Widget>;
    if (item.id === "updates") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-list">{model.updates.length ? model.updates.slice(0, 6).map((entry) => <button type="button" key={entry.device_id} onClick={() => openDevice(entry)}><span><strong>{entry.client_name} · {entry.device_name}</strong><small>{entry.detail}</small></span><em>{entry.title}</em></button>) : <p className="dashboard-empty">No hay actualizaciones pendientes informadas por los agentes.</p>}</div>
    </Widget>;
    if (item.id === "backups") {
      const groups = {
        SUCCESS: { label: "Correctas", statuses: ["SUCCESS"], tone: "ok" },
        WARNING: { label: "Con aviso", statuses: ["WARNING"], tone: "warn" },
        ERROR: { label: "Con error", statuses: ["ERROR", "MISSED"], tone: "error" },
      };
      const current = groups[backupDetail];
      const plans = current ? model.backupPlans.filter((plan) => current.statuses.includes(String(plan.backup_status || "").toUpperCase())) : [];
      const countFor = (key) => key === "ERROR" ? Number(model.backupStatus.ERROR || 0) + Number(model.backupStatus.MISSED || 0) : Number(model.backupStatus[key] || 0);
      return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-kpis dashboard-kpis--compact">
        {Object.entries(groups).map(([key, group]) => <button type="button" key={key} className={`dashboard-kpi-button ${backupDetail === key ? "is-active" : ""}`} onClick={() => setBackupDetail((currentKey) => currentKey === key ? "" : key)} aria-pressed={backupDetail === key}><span>{group.label}</span><strong className={group.tone !== "ok" && countFor(key) ? "is-warn" : ""}>{countFor(key)}</strong><small>Ver tareas</small></button>)}
      </div>
      {current ? <div className={`dashboard-backup-detail dashboard-backup-detail--${current.tone}`}><header><strong>{current.label}</strong><button type="button" onClick={() => setBackupDetail("")} aria-label="Cerrar detalle de copias">×</button></header>{plans.length ? <div className="dashboard-list">{plans.map((plan) => <button type="button" key={plan.id} onClick={() => openBackupPlan(plan)}><span><strong>{plan.name || "Tarea sin nombre"}</strong><small>{plan.client_name || "Cliente"}{plan.computer_name ? " · " + plan.computer_name : ""}</small></span><em>Abrir →</em></button>)}</div> : <p className="dashboard-empty">No hay tareas activas con este estado.</p>}</div> : null}
      <button type="button" className="button secondary small" onClick={openBackups}>Abrir copias</button>
    </Widget>;
    }
    if (item.id === "maintenance") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-list">{model.pending.length ? model.pending.map((item) => <button type="button" key={item.cliente_id || item.cliente} onClick={() => openMaintenance(item)}><span><strong>{item.cliente}</strong><small>{item.count} equipo{item.count === 1 ? "" : "s"} pendiente{item.count === 1 ? "" : "s"}</small></span><em>Abrir →</em></button>) : <p className="dashboard-empty">No hay mantenimientos pendientes.</p>}</div>
    </Widget>;
    if (item.id === "reports") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-list">{model.reports.length ? model.reports.map((item, index) => <button type="button" key={item.id || index} onClick={openReports}><span><strong>{displayName(item)}</strong><small>{item.mes || item.periodo || "Periodo sin informar"}</small></span><em>{Number(item.revisado) === 1 ? "Enviar" : "Revisar"}</em></button>) : <p className="dashboard-empty">No hay informes pendientes.</p>}</div>
    </Widget>;
    if (item.id === "avisos") return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-stat"><strong>{model.avisos.length} <small>en trámite</small></strong><span>{model.urgentAvisos.length ? model.urgentAvisos.length + " urgente" + (model.urgentAvisos.length === 1 ? "" : "s") : "Sin urgencias"}</span></div>
      <div className="dashboard-list">{model.avisos.length ? model.avisos.slice(0, 6).map((aviso) => <button type="button" key={aviso.id} onClick={openAvisos}><span><strong>#{aviso.numero_aviso || aviso.treyder_id} · {aviso.cliente_codigo_treyder || aviso.cliente_nombre_treyder || "Cliente"}</strong><small>{aviso.descripcion || "Sin indicaciones"}</small></span><em className={/urg|crit/i.test(String(aviso.prioridad || "")) ? "dashboard-severity-critical" : ""}>{aviso.prioridad || "Normal"}</em></button>) : <p className="dashboard-empty">No hay avisos en trámite.</p>}</div>
      <button type="button" className="button secondary small" onClick={openAvisos}>Abrir avisos</button>
    </Widget>;
    return <Widget key={item.id} {...widgetProps}>
      <div className="dashboard-list">{model.followups.length ? model.followups.slice(0, 6).map((item) => <button type="button" key={item.id} onClick={() => openMaintenance(item)}><span><strong>{displayName(item)}</strong><small>{item.incidencias || item.observaciones || "Requiere seguimiento"}</small></span><em>{item.status === "blocked" ? "Bloqueado" : "Seguir"}</em></button>) : <p className="dashboard-empty">No hay seguimientos pendientes.</p>}</div>
    </Widget>;
  };

  const available = Object.keys(CATALOG).filter((id) => !layout.widgets.some((item) => item.id === id));
  return <section className="dashboard-workspace">
    <ModuleHeader eyebrow="Inicio" title="Panel de trabajo" description="Prioriza tu jornada con la información que has elegido.">
      <div className="dashboard-actions">
        <button type="button" className="button secondary" onClick={load} disabled={loading}>↻ Actualizar</button>
        <button type="button" className={editing ? "button primary" : "button secondary"} onClick={() => setEditing((value) => !value)}>{editing ? "Terminar edición" : "Personalizar panel"}</button>
      </div>
    </ModuleHeader>
    {error ? <div className="dashboard-error">{error}</div> : null}
    {editing ? <section className="card dashboard-library"><div><strong>Añadir widgets</strong><span>El diseño se guarda solo para tu usuario.</span></div><div className="dashboard-library__items">{available.length ? available.map((id) => <button type="button" key={id} className="button secondary small" onClick={() => add(id)}>+ {CATALOG[id].title}</button>) : <small>Todos los widgets disponibles están en el panel.</small>}<button type="button" className="button secondary small" onClick={reset} disabled={saving}>Restablecer diseño</button></div></section> : null}
    {loading ? <div className="card dashboard-loading">Preparando tu panel de trabajo…</div> : <div className="dashboard-grid">{layout.widgets.map(renderWidget)}</div>}
  </section>;
}
